import React, { useState } from "react";
import { Button, Input, Select } from "../../../components";
import { LoadProfileUpload } from "./LoadProfileUpload";
import type { LoadPortfolioCreateInput, PhaseType } from "../domain/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  onSubmit: (data: LoadPortfolioCreateInput) => Promise<void>;
}

const INITIAL: LoadPortfolioCreateInput = {
  portfolio_name: "",
  latitude: 33.45,
  longitude: -112.07,
  flat_load_mw: 500,
  pue: 1.2,
  current_phase: "btm_only",
};

export function LoadPortfolioCreateModal({
  isOpen,
  onClose,
  customerId,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<LoadPortfolioCreateInput>({ ...INITIAL });
  const [loadMode, setLoadMode] = useState<"flat" | "upload">("flat");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const set = (field: keyof LoadPortfolioCreateInput, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (loadMode === "upload" && !form.load_profile_json) {
      setError("Please upload an 8760 load profile CSV.");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = { ...form, customer_id: customerId };
      if (loadMode === "flat") {
        delete submitData.load_profile_json;
      } else {
        delete submitData.flat_load_mw;
      }
      await onSubmit(submitData);
      setForm({ ...INITIAL });
      setLoadMode("flat");
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to create portfolio");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface-700 rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-h4 text-text-primary mb-4">
            Create Load Portfolio
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Portfolio Name"
              value={form.portfolio_name}
              onChange={(e) => set("portfolio_name", e.target.value)}
              placeholder="e.g., Phoenix Campus - Phase 1"
              required
            />

            <Input
              label="Description"
              value={form.description || ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Brief description"
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Latitude"
                type="number"
                step="0.001"
                value={form.latitude}
                onChange={(e) => set("latitude", parseFloat(e.target.value))}
                required
              />
              <Input
                label="Longitude"
                type="number"
                step="0.001"
                value={form.longitude}
                onChange={(e) => set("longitude", parseFloat(e.target.value))}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="BA Code"
                value={form.eia_ba_code || ""}
                onChange={(e) => set("eia_ba_code", e.target.value)}
                placeholder="e.g., ERCO"
              />
              <Input
                label="State"
                value={form.state || ""}
                onChange={(e) => set("state", e.target.value)}
                placeholder="e.g., TX"
                maxLength={2}
              />
            </div>

            {/* Load profile toggle */}
            <div>
              <label className="text-body-sm text-text-muted block mb-2">
                IT Load
              </label>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-lg text-body-sm transition-colors ${
                    loadMode === "flat"
                      ? "bg-quantum-500/20 text-quantum-400 border border-quantum-500/40"
                      : "bg-surface-600 text-text-muted border border-border hover:bg-surface-500"
                  }`}
                  onClick={() => setLoadMode("flat")}
                >
                  Flat Load
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-lg text-body-sm transition-colors ${
                    loadMode === "upload"
                      ? "bg-quantum-500/20 text-quantum-400 border border-quantum-500/40"
                      : "bg-surface-600 text-text-muted border border-border hover:bg-surface-500"
                  }`}
                  onClick={() => setLoadMode("upload")}
                >
                  Upload Profile
                </button>
              </div>

              {loadMode === "flat" ? (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="IT Load (MW)"
                    type="number"
                    step="0.1"
                    min="1"
                    value={form.flat_load_mw ?? 500}
                    onChange={(e) =>
                      set("flat_load_mw", parseFloat(e.target.value) || 500)
                    }
                    required
                  />
                  <Input
                    label="PUE"
                    type="number"
                    step="0.01"
                    min="1"
                    max="3"
                    value={form.pue}
                    onChange={(e) => set("pue", parseFloat(e.target.value))}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <LoadProfileUpload
                    currentFlatLoad={null}
                    currentProfile={form.load_profile_json || null}
                    onUpload={(profile) => set("load_profile_json", profile)}
                    onClearProfile={() => set("load_profile_json", undefined)}
                  />
                  <Input
                    label="PUE"
                    type="number"
                    step="0.01"
                    min="1"
                    max="3"
                    value={form.pue}
                    onChange={(e) => set("pue", parseFloat(e.target.value))}
                  />
                </div>
              )}
            </div>

            <Select
              label="Phase"
              value={form.current_phase}
              onChange={(e) =>
                set("current_phase", e.target.value as PhaseType)
              }
              options={[
                { value: "btm_only", label: "Behind-the-Meter Only" },
                { value: "interconnected", label: "Interconnected" },
              ]}
            />

            {error && (
              <div className="text-red-400 text-body-sm">{error}</div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create Portfolio"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
